import { Link } from "react-router-dom";
import { researchPillars } from "../data/researchPillars";
import { formatHtml } from "../utils/formatHtml";

export const DEFAULT_THEME_ICONS = {
  reflection: "💭",
  misalignment: "⚖️",
  agency: "🤝",
};

const hasHtml = (value) =>
  typeof value === "string" && /<a\s|<br|<b|<i|<u|<span/i.test(value);

const formatParagraphHtml = (text) =>
  formatHtml(String(text || ""))
    .replace(/\n\n/g, "<br/><br/>")
    .replace(/\n/g, "<br/>");

const Paragraph = ({ children, className = "" }) => {
  if (!children) return null;
  if (typeof children === "string" && (hasHtml(children) || children.includes("\n"))) {
    return (
      <p
        className={`text-sm leading-relaxed text-gray-700 dark:text-gray-300 [&_a]:text-green dark:[&_a]:text-green-dark [&_a]:underline [&_a]:underline-offset-2 ${className}`}
        dangerouslySetInnerHTML={{ __html: formatParagraphHtml(children) }}
      />
    );
  }
  return (
    <p className={`text-sm leading-relaxed text-gray-700 dark:text-gray-300 ${className}`}>
      {children}
    </p>
  );
};

const ThemeQuestion = ({ pillar, themeLinks }) => {
  if (themeLinks) {
    return (
      <Link
        to={`/home?view=agenda&pillar=${pillar.id}`}
        className="text-green dark:text-green-dark underline underline-offset-2"
      >
        {pillar.title}
      </Link>
    );
  }
  return pillar.title;
};

const ThemesList = ({ pitch, pillars, themeLinks }) => {
  const style = pitch.themesStyle || "bullets";
  const showLabels = pitch.themesShowLabels !== false;
  const icons = { ...DEFAULT_THEME_ICONS, ...(pitch.themeIcons || {}) };

  const label = (pillar) =>
    showLabels ? (
      <>
        <span className="font-semibold text-green dark:text-green-dark">
          {pillar.bigGoal || pillar.id}
        </span>
        {": "}
      </>
    ) : null;

  if (style === "plain") {
    return (
      <div className="mt-2 space-y-1">
        {pillars.map((pillar) => (
          <p
            key={pillar.id}
            className="text-sm leading-relaxed text-gray-700 dark:text-gray-300"
          >
            {label(pillar)}
            <ThemeQuestion pillar={pillar} themeLinks={themeLinks} />
          </p>
        ))}
      </div>
    );
  }

  if (style === "icons") {
    return (
      <ul className="mt-2 space-y-1">
        {pillars.map((pillar) => (
          <li
            key={pillar.id}
            className="flex gap-2.5 text-sm leading-relaxed text-gray-700 dark:text-gray-300"
          >
            <span className="mt-0.5 w-6 shrink-0 text-center text-base" aria-hidden>
              {icons[pillar.id] || "•"}
            </span>
            <span>
              {label(pillar)}
              <ThemeQuestion pillar={pillar} themeLinks={themeLinks} />
            </span>
          </li>
        ))}
      </ul>
    );
  }

  const listClass =
    style === "numbered"
      ? "mt-2 list-decimal space-y-1 pl-4"
      : "mt-2 list-disc space-y-1 pl-4";

  return (
    <ul className={`${listClass} text-sm leading-relaxed text-gray-700 dark:text-gray-300`}>
      {pillars.map((pillar) => (
        <li key={pillar.id}>
          {label(pillar)}
          <ThemeQuestion pillar={pillar} themeLinks={themeLinks} />
        </li>
      ))}
    </ul>
  );
};

/**
 * Home research pitch.
 * Theme list is driven by researchPillars; formatting comes from homePitch.
 */
const HomeBio = ({
  pitch,
  themeLinks: themeLinksProp,
  pillars = researchPillars,
}) => {
  const themeLinks =
    themeLinksProp !== undefined
      ? themeLinksProp
      : pitch.themesLinkToResearch !== false;

  return (
    <div className="text-left">
      <Paragraph>{pitch.greeting}</Paragraph>
      <Paragraph className="mt-3">{pitch.researchLead}</Paragraph>

      {pitch.themesLead && (
        <Paragraph className="mt-3">{pitch.themesLead}</Paragraph>
      )}
      <ThemesList pitch={pitch} pillars={pillars} themeLinks={themeLinks} />

      <Paragraph className="mt-3">{pitch.supervisors}</Paragraph>
      <Paragraph className="mt-3">{pitch.grounding}</Paragraph>
      {pitch.proof && <Paragraph className="mt-3">{pitch.proof}</Paragraph>}
      <Paragraph className="mt-3">{pitch.closing}</Paragraph>
      {pitch.invite && <Paragraph className="mt-3">{pitch.invite}</Paragraph>}
    </div>
  );
};

export { Paragraph as BioParagraph };
export default HomeBio;
